import React, { memo, useState } from 'react';
import { View, Text, Image, Pressable, Platform } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import type { ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { getApiText, useTranslation, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { resolveAttachmentUri } from '../../shared/utils/resolveMediaUri';
import type { PlanSection } from '../../data/domains/subscription';
import type { SubscriptionListStyles } from './subscriptionListStyles';

type Props = {
  section: PlanSection;
  colors: ThemeColors;
  styles: SubscriptionListStyles;
  onPress: () => void;
};

export const SubscriptionCatalogCategoryCard = memo(function SubscriptionCatalogCategoryCard({
  section,
  colors,
  styles,
  onPress,
}: Props) {
  const { t } = useTranslation();
  const { chevronNextAlongAxis, textAlign, writingDirection } = useRtl();
  const [image_failed, set_image_failed] = useState(false);

  const title =
    section.title === 'Plans' ? t('subscriptions.plans') : getApiText(section as unknown as Record<string, unknown>, 'title', 'arabic_title') || section.title;
  const description = getApiText(section as unknown as Record<string, unknown>, 'description', 'arabic_description') || section.description;
  const image_uri = resolveAttachmentUri(section.attachment) ?? resolveAttachmentUri((section as { thumbnail?: unknown }).thumbnail) ?? undefined;
  const plan_count = section.data?.length ?? 0;
  const ripple_color = hexToRgba(colors.primary, 0.1) as string;

  return (
    <Pressable
      onPress={onPress}
      android_ripple={{ color: ripple_color }}
      style={({ pressed }) => [styles.tagFlowCategoryCard, pressed && Platform.OS === 'ios' ? { opacity: 0.94 } : null]}
      accessibilityRole="button"
      accessibilityLabel={String(title)}>
      <View style={styles.tagFlowCategoryCardInner}>
        <View style={styles.tagFlowCategoryCardImageWrap}>
          {image_uri && !image_failed ? (
            <Image
              source={{ uri: image_uri }}
              style={styles.tagFlowCategoryCardImage}
              resizeMode="cover"
              fadeDuration={0}
              onError={() => set_image_failed(true)}
            />
          ) : (
            <View style={styles.tagFlowCategoryCardPlaceholder} />
          )}
        </View>
        <View style={styles.tagFlowCategoryCardBody}>
          <Text style={[styles.tagFlowCategoryCardTitle, { textAlign, writingDirection }]} numberOfLines={2}>
            {title}
          </Text>
          {description ? (
            <Text style={[styles.tagFlowCategoryCardDescription, { textAlign, writingDirection }]} numberOfLines={2}>
              {description}
            </Text>
          ) : null}
          <View style={styles.tagFlowCategoryCardMeta}>
            <View style={styles.tagFlowCategoryCardMetaBox}>
              <Text style={styles.tagFlowCategoryCardMetaQty}>{formatNumber(plan_count)}</Text>
              <Text style={styles.tagFlowCategoryCardMetaLabel}>{t('subscriptions.plans')}</Text>
            </View>
          </View>
        </View>
        <SvgIcon name={chevronNextAlongAxis} size={20} color={colors.textSecondary} style={styles.tagFlowCategoryCardChevron} />
      </View>
    </Pressable>
  );
});
